/* eslint-disable react/display-name */
import { useMenuContext } from 'app/[lang]/_contexts/MenuContext';
import { Button, DateRange, Hint, Input, Label, Select, Textarea } from 'components';
import Accordion from 'components/Accordion';
import { DateValueType } from 'components/Inputs/DateRange/Datepicker';
import { POLL_TIME } from 'constants/constants';
import { getMetadataHubDb } from 'factories/getMetadataHubDb';
import { getTargetLanguagesHubDb } from 'factories/getTargetLanguagesHubDb';
import {
  AiWorkflow,
  ContentSourceEnum,
  InputMaybe,
  IntegrationEnum,
  Maybe,
  MetadataContextEnum,
  OrderableTargetLanguageCodesEnum,
  QualityEnum,
  Scalars,
  UploadedFile
} from 'gql/graphql';
import { toaster } from 'helpers/toaster';
import useOptionsAgents from 'hooks/useOptionsAgents';
import { decamelize, pascalize } from 'humps';
import { useTranslations } from 'next-intl';
import { useSearchParams } from 'next/navigation';
import { useAgentsList } from 'providers/AgentsProviders/useAgentsList';
import { useFeatureFlags } from 'providers/ClientProviders/useFeatureFlags';
import { useTranslationRequestCopyOrderDetails } from 'providers/TranslationRequests/useTranslationRequestCopyOrderDetails';
import { useTranslationRequestCopyOrderDetailsResult } from 'providers/TranslationRequests/useTranslationRequestCopyOrderDetailsResult';
import { useTranslationRequestFileOrderDetails } from 'providers/TranslationRequests/useTranslationRequestFileOrderDetails';
import { useTranslationRequestFileOrderDetailsResult } from 'providers/TranslationRequests/useTranslationRequestFileOrderDetailsResult';
import { useUploadedFileMetadataUpdate } from 'providers/TranslationRequests/useUploadedFileMetadataUpdate';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { Elements, StripeProvider } from 'react-stripe-elements';
import { notifyException } from 'services/appsignal';
import { KeysEnum } from 'types/KeysEnum';
import { extractParam } from 'utils/paramsUtil';
import { hubDbValidation } from 'validations/hubDbValidation';
import { TInputs } from '../page';
import {
  getParamsCreateTr,
  updateInputField,
  updateOrderDetails,
  updateSidebarFlow,
  updateWordCount
} from './_actions';
import { CONTENT_FLOWS, SIDEBAR_FLOWS } from './_consts';
import {
  getCanBeCalculated,
  getColumns,
  getCreateTrParams,
  getExcludedRanges,
  getIsAFile,
  getIsPlainTextConfigurable,
  getOrderableQualities,
  getSelectedFile,
  getTotalRows
} from './_selectors';
import { columnToChars, isFullColumn } from './_utils';
import CharacterLimit from './CharacterLimit';
import Excluder from './Excluder';
import HubDbColumnMapping, { ColumnMapping } from './HubDbColumnMapping';
import LanguagePairs from './LanguagePairs';
import OrderDetails from './OrderDetails';
import PaymentDetails from './PaymentDetails';
import SelectEditors from './SelectEditors';
import SelectGlossaries from './SelectGlossaries';
import ToggleInput from './ToggleInput';

const ACCEPTED_FILES = ['xlsx', 'csv'];

export interface ITrParams extends TInputs {
  keyValueData?: { char_limit: [string, number][] };
  editorIdsPerLanguage: Record<OrderableTargetLanguageCodesEnum, string>;
  editorPerLanguage: boolean;
  generalEditorId: string;
  plainText: boolean;
  assignedGlossariesIds: string[];
  uploadedFileIds?: string[];
  isAFile: boolean;
  integration: IntegrationEnum;
  included?: InputMaybe<Array<Scalars['String']>>;
}

interface ISidebar {
  inputs: TInputs;
  excludedRanges: any[];
  totalRows: number;
  selectedFile: any;
  updateInputField: any;
  styleGuides: { referenceId: number; name: string }[];
  qualities: QualityEnum[];
  contentFlow: string;
  sidebarFlow: string;
  orderable: boolean;
  isAFile: boolean;
  integration: string | undefined;
  isPlainTextConfigurable: boolean;
  validationParams: any;
  updateSidebarFlow: any;
  updateOrderDetails: any;
  updateWordCount: any;
  columns: any[];
  getParamsCreateTr: () => any;
  filesUploaded?: Maybe<Array<UploadedFile> | undefined>;
  metadata: Record<string, any>[];
  columnMapping: ColumnMapping[];
}

const Sidebar: FC<ISidebar> = ({
  inputs,
  excludedRanges,
  totalRows,
  selectedFile,
  updateInputField,
  styleGuides,
  qualities,
  contentFlow,
  sidebarFlow,
  orderable,
  isAFile,
  columns,
  metadata,
  integration,
  isPlainTextConfigurable,
  updateSidebarFlow,
  updateOrderDetails,
  updateWordCount,
  filesUploaded,
  columnMapping,
  getParamsCreateTr
}) => {
  const t = useTranslations();
  const searchParams = useSearchParams();
  const type = extractParam(searchParams, KeysEnum.Type, '');
  const ids = extractParam(searchParams, KeysEnum.Ids, []) as string[];

  const { handleScrollTop } = useMenuContext();

  const { mutateAsync: translationRequestCopyOrderDetails } = useTranslationRequestCopyOrderDetails(
    {
      onError: (error: unknown) => {
        notifyException(error);
        toaster.error(t('messages.error.order_details'));
      }
    }
  );

  const { mutateAsync: translationRequestFileOrderDetails } = useTranslationRequestFileOrderDetails(
    {
      onError: (error) => {
        notifyException(error);
        toaster.error(t('messages.error.order_details'));
      }
    }
  );

  const { mutateAsync: uploadedFileMetadataUpdate } = useUploadedFileMetadataUpdate({
    onError: (error) => {
      notifyException(error);
      toaster.error(t('messages.error.hubdb'));
    }
  });

  const [copyGuid, setCopyGuid] = useState('');
  const [uploadedFilesGuid, setUploadedFilesGuid] = useState('');

  const { data: featureFlags } = useFeatureFlags();

  const { optionsAgents } = useOptionsAgents({ active: true });
  const { data: agents } = useAgentsList({ active: true });

  const { data: fileOrderDetails } = useTranslationRequestFileOrderDetailsResult(
    {
      guid: uploadedFilesGuid
    },
    {
      enabled: !!uploadedFilesGuid,
      staleTime: 0,
      refetchInterval: !!uploadedFilesGuid ? POLL_TIME : false
    }
  );

  const { data: copyOrderDetailsData } = useTranslationRequestCopyOrderDetailsResult(
    {
      guid: copyGuid
    },
    {
      enabled: !!copyGuid,
      staleTime: 0,
      refetchInterval: !!copyGuid ? POLL_TIME : false
    }
  );

  const [state, setState] = useState({
    showingAdvanced: false,
    errors: {} as Record<string, boolean | string[]>,
    validatingParams: false
  });

  const [selectedAgent, setSelectedAgent] = useState<AiWorkflow | undefined>();

  const {
    name,
    quality,
    workflowId,
    styleGuideReferenceId,
    sendCompletionEmail,
    htmlAllowed,
    proposedClientDeadline,
    contextUrl,
    callbackUrl,
    autoClientReview,
    comment,
    charLimit,
    purchaseOrderCode
  } = inputs;

  const isHubDbIntegration =
    integration === IntegrationEnum.Hubdb &&
    type == MetadataContextEnum.Hubdb &&
    ids?.every(String);

  const defaultQuality = qualities.find((item) => item === QualityEnum.Localization);

  const isOneOfAcceptedFiles = ACCEPTED_FILES.includes(selectedFile?.contentType?.toLowerCase());
  const charColumns = Array.from({ length: selectedFile?.totalColumns ?? 0 }, (_, index) =>
    columnToChars(index + 1)
  );

  const excludedColumns = excludedRanges.reduce((acc, range) => {
    if (isFullColumn(range, totalRows)) return [...acc, columnToChars(range.start.column)];

    return acc;
  }, []);

  const handleAgentChange = (option: any) => {
    if (!option || !option?.value || option?.value === '') {
      setSelectedAgent(undefined);
      return updateInputField({ field: 'workflowId', value: '' });
    }
    const agentId = option.value;
    const agent = agents?.nodes?.find((n) => (n.id as string) === agentId);

    updateInputField({ field: 'workflowId', value: agentId });
    setSelectedAgent(agent);
    if (agent?.postediting) {
      qualities.push(QualityEnum.Postediting);
      return updateInputField({ field: 'quality', value: QualityEnum.Postediting });
    } else {
      updateInputField({ field: 'quality', value: QualityEnum.Machine });
    }
  };

  const handleChangeDateRange = (value: DateValueType) =>
    updateInputField({
      field: 'proposedClientDeadline',
      value: value?.startDate ? new Date(value?.startDate ?? '').toISOString() : ''
    });

  useEffect(() => {
    if (charColumns.length > 0 && Object.keys(charLimit).length === 0)
      updateInputField({
        field: 'charLimit',
        value: charColumns.reduce((acc, charColumn) => ({ ...acc, [charColumn]: null }), {})
      });
  }, [charColumns, charLimit]);

  const getTranslationRequestCopyOrderDetails = async (data: any) =>
    translationRequestCopyOrderDetails(data);

  const getTranslationRequestFileOrderDetails = async (data: any, params: any) =>
    translationRequestFileOrderDetails({
      ...data,
      uploadedFileIds: params.uploadedFileIds,
      excluded: params.excluded
    });

  const handleShowOrderDetails = async () => {
    handleScrollTop();
    const params: ITrParams = await getParamsCreateTr();

    const data = {
      contentSource: params.isAFile ? ContentSourceEnum.UploadedFileId : ContentSourceEnum.Copy,
      quality: params.quality as QualityEnum,
      copy: params.copy,
      autoClientReview: params.autoClientReview,
      purchaseOrderCode: params.purchaseOrderCode,
      sendCompletionEmail: params.sendCompletionEmail,
      sourceLanguageCode: params.sourceLanguageCode,
      targetLanguageCodes: params.targetLanguageCodes,
      name: params.name,
      proposedClientDeadline: params.proposedClientDeadline,
      styleGuideReferenceId: params.styleGuideReferenceId,
      generalEditorId: params.generalEditorId,
      plainText: params.plainText,
      editorPerLanguage: params.editorPerLanguage,
      assignedGlossariesIds: params.assignedGlossariesIds,
      callbackUrl: params.callbackUrl,
      contextUrl: params.contextUrl,
      comment: params.comment,
      editorIdsPerLanguage: params.editorIdsPerLanguage,
      keyValueData: params.keyValueData,
      integration: params.integration,
      workflowId: params.workflowId,
      included: params.included
    };

    if (params.isAFile && isHubDbIntegration) {
      const updatedMetadata = getMetadataHubDb(metadata, columnMapping, selectedFile);
      const validationHubDb = hubDbValidation(updatedMetadata, columnMapping);

      if (!validationHubDb?.isValid) return toaster.error(t('messages.error.column_mapping'));

      const targetLanguagesHubDb = getTargetLanguagesHubDb(columnMapping);

      updateInputField({
        field: 'targetLanguageCodes',
        value: targetLanguagesHubDb
      });

      const [responseTr] = await Promise.all([
        getTranslationRequestFileOrderDetails(
          { ...data, targetLanguageCodes: targetLanguagesHubDb },
          { ...params, targetLanguageCodes: targetLanguagesHubDb }
        ),
        uploadedFileMetadataUpdate(updatedMetadata)
      ]);

      setUploadedFilesGuid(`${responseTr?.guid}/result`);
    } else if (params.isAFile) {
      const response = await getTranslationRequestFileOrderDetails(data, params);
      setUploadedFilesGuid(`${response.guid}/result`);
    } else {
      const response = await getTranslationRequestCopyOrderDetails(data);
      setCopyGuid(`${response.guid}/result`);
    }

    setState((oldState) => ({ ...oldState, errors: {} }));
    updateSidebarFlow(SIDEBAR_FLOWS.ORDER_DETAILS);
    return setState((oldState) => ({ ...oldState, validatingParams: false }));
  };

  const handleFileOrderDetails = useCallback(() => {
    if (fileOrderDetails?.orderDetails) {
      setUploadedFilesGuid('');
      updateWordCount(fileOrderDetails.wordCount);
      updateOrderDetails(fileOrderDetails.orderDetails);
    }
  }, [
    fileOrderDetails?.orderDetails,
    fileOrderDetails?.wordCount,
    updateOrderDetails,
    updateWordCount
  ]);

  const handleCopyOrderDetails = useCallback(() => {
    if (copyOrderDetailsData?.orderDetails) {
      setCopyGuid('');
      updateWordCount(copyOrderDetailsData.wordCount);
      updateOrderDetails(copyOrderDetailsData.orderDetails);
    }
  }, [
    copyOrderDetailsData?.orderDetails,
    copyOrderDetailsData?.wordCount,
    updateOrderDetails,
    updateWordCount
  ]);

  const handleUpdateCharLimit = useCallback(
    (char: string, value: string) =>
      updateInputField({
        field: 'charLimit',
        value: { ...charLimit, [char]: value }
      }),
    [charLimit, updateInputField]
  );

  const selectedStyleGuideReferenceId = useMemo(() => {
    if (!selectedAgent || !workflowId) return styleGuideReferenceId;
    if (selectedAgent) return String(selectedAgent.styleGuide?.referenceId);

    return styleGuideReferenceId;
  }, [selectedAgent, styleGuideReferenceId, workflowId]);

  useEffect(() => {
    handleFileOrderDetails();
    handleCopyOrderDetails();
  }, [handleFileOrderDetails, handleCopyOrderDetails]);

  // Order Details
  // ==========================================
  if (sidebarFlow === SIDEBAR_FLOWS.ORDER_DETAILS) return <OrderDetails />;

  // Payment Details
  // ==========================================
  if (sidebarFlow === SIDEBAR_FLOWS.PAYMENT_DETAILS)
    return (
      <StripeProvider apiKey={process.env.NEXT_PUBLIC_STRIPE_PUBLIC_KEY}>
        <Elements>
          <PaymentDetails />
        </Elements>
      </StripeProvider>
    );

  // Inputs
  // ==========================================
  return (
    <div className="ordering_form__sidebar rounded shadow-custom">
      {/* Name */}
      <>
        <Label className="!mt-0">{t('ordering_form.name')}</Label>
        <Input
          className={'mb-2'}
          error={!!state.errors['name']}
          value={name}
          onChange={(e) => updateInputField({ field: 'name', value: e.target.value })}
          type="text"
          placeholder={t('ordering_form.name')}
        />
        <Hint className="mb-2">{t('ordering_form.name_explanation')}</Hint>
      </>
      {/* Language Pairs */}
      <LanguagePairs hideTargetLanguages={isHubDbIntegration} />

      {/* Language of columns */}
      {isHubDbIntegration && <HubDbColumnMapping />}

      {/* Excluder */}
      {contentFlow == CONTENT_FLOWS.TABLE && <Excluder />}

      {/* Agents */}
      {featureFlags?.agentsEnabled && (
        <>
          <Label className="!mt-0">{t('labels.agents')}</Label>
          <Select
            placeholder={t('placeholders.select_agent')}
            options={optionsAgents}
            selectedValue={workflowId}
            onChange={handleAgentChange}
          />
          <Hint className="mb-2">{t('hints.select_agents')}</Hint>
        </>
      )}
      {/* Quality */}
      <>
        <Label>{t('ordering_form.quality')}</Label>
        <Select
          id="quality"
          options={qualities.map((value) => ({ value, label: pascalize(value) }))}
          selectedValue={quality}
          onChange={({ value }) => updateInputField({ field: 'quality', value: value })}
          defaultOption={{ value: defaultQuality, label: pascalize(defaultQuality ?? '') }}
          disabled={!!workflowId}
        />
        <Hint className="mb-2">{t(`ordering_form.quality_explanations.${quality}` as any)}</Hint>
      </>
      {/* Style Guide */}
      <>
        <Label>{t('ordering_form.style_guide')}</Label>
        <Hint className="mb-2">{t('ordering_form.style_guide_explanation')}</Hint>
        <Select
          id="styleGuideReferenceId"
          options={styleGuides?.map((sg) => ({ value: String(sg.referenceId), label: sg.name }))}
          selectedValue={selectedStyleGuideReferenceId}
          onChange={({ value }) =>
            updateInputField({ field: 'styleGuideReferenceId', value: value })
          }
          disabled={!!workflowId}
        />
      </>
      {/* Assigned glossaries */}
      <SelectGlossaries selectedAgent={selectedAgent} workflowId={workflowId} />
      {/* Send email on completion */}
      <>
        <Label>{t('ordering_form.send_email_on_completion')}</Label>
        <ToggleInput
          checked={sendCompletionEmail}
          name="send_email_on_completion"
          onChange={(value) => updateInputField({ field: 'sendCompletionEmail', value })}
        />
      </>
      {/* HTML enabled */}
      {isPlainTextConfigurable && (
        <>
          <Label>{t('ordering_form.html_enabled')}</Label>
          <ToggleInput
            checked={htmlAllowed}
            name="html_enabled"
            onChange={(value) => updateInputField({ field: 'htmlAllowed', value })}
          />
          <Hint className="mb-2">
            {t('ordering_form.html_enabled_explanation').replace('&gt;', '>').replace('&lt;', '<')}
          </Hint>
        </>
      )}
      <div className="relative z-10">
        <Accordion title={t('ordering_form.advanced_options')}>
          <div className="ordering_form__advanced ">
            <>
              {/* Preferred date / Proposed deadline */}
              <Label className="!mt-0">{t('ordering_form.preferred_date')}</Label>
              <DateRange
                className="mb-2"
                value={{
                  startDate: proposedClientDeadline ? proposedClientDeadline : null,
                  endDate: proposedClientDeadline ? proposedClientDeadline : null
                }}
                onChange={handleChangeDateRange}
                minDate={new Date()}
                asSingle
                asTimePicker
                showFooter
                noYesterday
                placeholder="--/--/---- --:-- --"
                displayFormat="DD/MM/YYYY hh:mm A"
                name="proposedClientDeadline"
              />
              <Hint className="mb-2">{t('ordering_form.preferred_date_explanation')}</Hint>
            </>

            {/* Context url */}
            <>
              <Label>{t('ordering_form.context_url')}</Label>
              <Hint className="mb-2">{t('ordering_form.context_url_explanation')}</Hint>
              <Input
                error={!!state.errors['contextUrl']}
                value={contextUrl}
                onChange={(e) => updateInputField({ field: 'contextUrl', value: e.target.value })}
                type="text"
                className={'mb-1'}
                placeholder={t('ordering_form.context_url')}
              />
            </>

            {/* Callback url */}
            <>
              <Label>{t('ordering_form.callback_url')}</Label>
              <Hint className="mb-2">{t('ordering_form.callback_url_explanation')}</Hint>
              <Input
                error={!!state.errors['callbackUrl']}
                value={callbackUrl}
                onChange={(e) => updateInputField({ field: 'callbackUrl', value: e.target.value })}
                type="text"
                className={'mb-1'}
                placeholder={t('ordering_form.callback_url')}
              />
            </>

            {/* Auto accept */}
            <>
              <Label>{t('ordering_form.auto_accept')}</Label>
              <ToggleInput
                checked={autoClientReview}
                name="auto_accept"
                onChange={(value) => updateInputField({ field: 'autoClientReview', value })}
              />
            </>

            {/* Translators comment */}
            <>
              <Label>{t('ordering_form.translators_comment')}</Label>
              <Hint className="mb-2">{t('ordering_form.translators_comment_explanation')}</Hint>
              <Textarea
                value={comment}
                onChange={(e) => updateInputField({ field: 'comment', value: e.target.value })}
                placeholder={t('ordering_form.translators_comment')}
              />
            </>

            {isAFile && charColumns.length > 0 && isOneOfAcceptedFiles && (
              <div className={'mb-3'}>
                {/* Character limit */}
                <CharacterLimit
                  charColumns={charColumns}
                  charLimit={charLimit}
                  errors={!!state.errors['charLimit']}
                  excludedColumns={excludedColumns}
                  handleUpdateCharLimit={handleUpdateCharLimit}
                />
              </div>
            )}

            {/* Purchase order */}
            <div>
              <Label>{t('ordering_form.purchase_order')}</Label>
              <Input
                className={'mb-1'}
                error={!!state.errors['purchaseOrderCode']}
                value={purchaseOrderCode}
                onChange={(e) =>
                  updateInputField({ field: 'purchaseOrderCode', value: e.target.value })
                }
                type="text"
                placeholder={t('ordering_form.purchase_order')}
              />
              <Hint className="mb-2">{t('ordering_form.purchase_order_explanation')}</Hint>
            </div>

            {/* Assigned editor */}
            <>
              <SelectEditors />
            </>
          </div>
        </Accordion>
      </div>
      {!!Object.keys(state.errors).length && (
        <>
          <h3 className="color-danger ordering_form__details_title">
            <span className="ticon-warning mr-2" />
            {t('ordering_form.please_correct')}
          </h3>
          <ul className="disc_list mb-3">
            {Object.keys(state.errors).map((key, index) => {
              const error = Array.isArray(state.errors[key])
                ? Array(state.errors[key]).join(', ')
                : '';

              return (
                <li key={index}>
                  <strong className="mr-1">{t(`ordering_form.${decamelize(key)}` as any)}:</strong>{' '}
                  {error}.
                </li>
              );
            })}
          </ul>
        </>
      )}
      <div className="mt-4 flex items-center justify-center">
        <Button
          variant="contained"
          disabled={!orderable || state.validatingParams}
          onClick={handleShowOrderDetails}
          className="w-full"
        >
          {t('ordering_form.show_order_details')}
        </Button>
      </div>
    </div>
  );
};

export default connect(
  (state: {
    inputs: TInputs;
    contentFlow: any;
    sidebarFlow: any;
    fileData: any;
    integration: string | undefined;
    staticData: {
      styleGuides: { referenceId: number; name: string }[];
      editors: { id: number; name: string }[];
    };
    filesUploaded?: Maybe<Array<UploadedFile> | undefined>;
    metadata: Record<string, any>[];
    columnMapping: ColumnMapping[];
  }) => {
    const {
      inputs,
      contentFlow,
      sidebarFlow,
      fileData,
      inputs: { integration },
      staticData: { styleGuides, editors },
      filesUploaded,
      metadata,
      columnMapping
    } = state;

    return {
      fileData,
      metadata,
      columnMapping,
      contentFlow,
      editors,
      inputs,
      sidebarFlow,
      styleGuides,
      integration,
      filesUploaded,
      isAFile: getIsAFile(state),
      orderable: getCanBeCalculated(state),
      qualities: getOrderableQualities(state),
      validationParams: getCreateTrParams(state),
      isPlainTextConfigurable: getIsPlainTextConfigurable(state),
      columns: getColumns(state),
      excludedRanges: getExcludedRanges(state),
      totalRows: getTotalRows(state),
      selectedFile: getSelectedFile(state)
    };
  },
  { updateInputField, updateSidebarFlow, updateOrderDetails, updateWordCount, getParamsCreateTr }
)(Sidebar);
